import React, { Suspense, useRef } from "react";
import { Canvas, useLoader, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Text, Billboard, RoundedBox } from "@react-three/drei";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";

function Model({ url, position, rotation, scale }) {
  const gltf = useLoader(GLTFLoader, url);
  // Traverse the model to enable shadows on all meshes
  React.useEffect(() => {
    gltf.scene.traverse((node) => {
      if (node.isMesh) {
        node.castShadow = true;
        node.receiveShadow = true;
      }
    });
  }, [gltf]);

  return (
    <primitive
      object={gltf.scene}
      scale={scale}
      position={position}
      rotation={rotation}
    />
  );
}

function CircularPlatform() {
  return (
    <>
      {/* Bottom layer - largest */}
      <mesh position={[0, -0.2, 0]} receiveShadow>
        <cylinderGeometry args={[3.4, 3.4, 0.1, 64]} />
        <meshStandardMaterial color="#d0d7de" />
      </mesh>
      
      {/* Middle layer */}
      <mesh position={[0, -0.1, 0]} receiveShadow>
        <cylinderGeometry args={[3.2, 3.2, 0.1, 64]} />
        <meshStandardMaterial color="#e6edf3" />
      </mesh>
      
      {/* Top layer - smallest */}
      <mesh position={[0, 0, 0]} receiveShadow>
        <cylinderGeometry args={[3, 3, 0.1, 64]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
    </>
  );
}

function Column({ position, height, color, opacity = 1, isCurrentDay = false, delay = 0 }) {
  const materialRef = useRef();
  const columnRef = useRef();
  const animationRef = useRef({
    started: false,
    startTime: 0,
    duration: 1.5, // Animation duration in seconds
    initialHeight: 0.2,
    peakHeight: 1.3,
    finalHeight: height,
    delay: delay // Add delay property
  });

  // Define levels here so we can use them for color interpolation
  const levels = [
    { color: '#ebedf0', minHeight: 0.1, maxHeight: 0.2 },   // Level 1 - lowest
    { color: '#9be9a8', minHeight: 0.3, maxHeight: 0.5 },   // Level 2
    { color: '#40c463', minHeight: 0.6, maxHeight: 0.7 },   // Level 3
    { color: '#30a14e', minHeight: 0.8, maxHeight: 1.0 },   // Level 4
    { color: '#216e39', minHeight: 1.1, maxHeight: 1.3 }    // Level 5 - highest
  ];

  // Helper function to get color based on current height
  const getColorForHeight = (height) => {
    for (let i = levels.length - 1; i >= 0; i--) {
      if (height >= levels[i].minHeight) {
        return levels[i].color;
      }
    }
    return levels[0].color;
  };
  
  useFrame((state) => {
    if (isCurrentDay && materialRef.current) {
      // Existing current day animation
      materialRef.current.opacity = 0.75 + Math.sin(state.clock.elapsedTime * 2) * 0.25;
    }

    // Wait for delay before starting animation
    if (!animationRef.current.started && state.clock.elapsedTime >= animationRef.current.delay) {
      animationRef.current.started = true;
      animationRef.current.startTime = state.clock.elapsedTime;
    }

    if (!animationRef.current.started) {
      // Keep initial state until animation starts
      columnRef.current.scale.y = animationRef.current.initialHeight;
      columnRef.current.position.y = animationRef.current.initialHeight / 2;
      if (materialRef.current) {
        materialRef.current.color.set(getColorForHeight(animationRef.current.initialHeight));
      }
      return;
    }

    const elapsed = state.clock.elapsedTime - animationRef.current.startTime;
    const { duration, initialHeight, peakHeight, finalHeight } = animationRef.current;

    if (elapsed <= duration) {
      let progress;
      let currentHeight;
      
      if (elapsed < duration / 2) {
        // First half: animate from initial to peak height
        progress = elapsed / (duration / 2);
        currentHeight = initialHeight + (peakHeight - initialHeight) * progress;
      } else {
        // Second half: animate from peak to final height
        progress = (elapsed - duration / 2) / (duration / 2);
        currentHeight = peakHeight + (finalHeight - peakHeight) * progress;
      }
      
      columnRef.current.scale.y = currentHeight;
      columnRef.current.position.y = currentHeight / 2;
      
      // Update color based on current height
      if (materialRef.current) {
        materialRef.current.color.set(getColorForHeight(currentHeight));
      }
    }
  });

  return (
    <mesh 
      ref={columnRef}
      position={[position[0], height / 2, position[2]]}
      castShadow
      receiveShadow
      scale={[1, 1, 1]}
    >
      <boxGeometry args={[0.25, 1, 0.25]} />
      <meshStandardMaterial 
        ref={materialRef}
        color={color} 
        transparent={true}
        opacity={isCurrentDay ? 0.75 : opacity}
      />
    </mesh>
  );
}

function ColumnsGrid() {
  // Get current date and calculate dates for the last 8 weeks
  const today = new Date();
  const dayOfWeek = today.getDay();
  // Adjust Sunday from 0 to 6 to match Monday=0 to Sunday=6 format
  const adjustedDayOfWeek = (dayOfWeek + 6) % 7;
  
  // Parse URL parameters for contribution data
  const urlParams = new URLSearchParams(window.location.search);
  const dataParam = urlParams.get('data');
  const data = dataParam ? JSON.parse(dataParam) : null;
  
  const columns = [];
  const weeks = 8;
  const daysPerWeek = 7;
  const spacing = 0.265; // Matches the spacing used in DayLabels
  
  // Define contribution levels with color and height ranges
  const levels = [
    { color: '#ebedf0', minHeight: 0.1, maxHeight: 0.2 },   // Level 1 - lowest
    { color: '#9be9a8', minHeight: 0.3, maxHeight: 0.5 },   // Level 2
    { color: '#40c463', minHeight: 0.6, maxHeight: 0.7 },   // Level 3
    { color: '#30a14e', minHeight: 0.8, maxHeight: 1.0 },   // Level 4
    { color: '#216e39', minHeight: 1.1, maxHeight: 1.3 }   // Level 5 - highest
  ];
  
  for (let week = weeks - 1; week >= 0; week--) {
    for (let day = 0; day < daysPerWeek; day++) {
      // Skip future days in the current week
      if (week === 0 && day > adjustedDayOfWeek) continue;
      
      // Calculate delay based on position
      // Start from back-right corner (0.0s) to front-left corner (max delay)
      const delay = ((weeks - 1 - week) + (daysPerWeek - 1 - day)) * 0.1;
      
      let level, height;
      
      // Calculate index in the data array
      const index = (weeks - 1 - week) * daysPerWeek + day;
      
      if (data && data[index] !== undefined) {
        // Use provided contribution value to determine level (1-5)
        const value = data[index];
        // Adjust value to 0-4 index for levels array
        level = levels[Math.min(Math.max(value - 1, 0), levels.length - 1)];
        height = level.minHeight + (level.maxHeight - level.minHeight) / 2;
      } else {
        // Fallback to random level and height
        const levelIndex = Math.floor(Math.random() * levels.length);
        level = levels[levelIndex];
        height = level.minHeight + Math.random() * (level.maxHeight - level.minHeight);
      }
      
      // Check if this is the current day's column
      const isCurrentDay = week === 0 && day === adjustedDayOfWeek;
      
      columns.push(
        <Column
          key={`column-${week}-${day}`}
          position={[
            ((weeks - 1 - week) - weeks/2) * spacing,
            0,
            ((daysPerWeek - 1 - day) - daysPerWeek/2) * spacing
          ]}
          height={height}
          color={level.color}
          opacity={isCurrentDay ? 0.5 : 1} // Make current day more transparent
          isCurrentDay={isCurrentDay}
          delay={delay} // Pass the calculated delay
        />
      );
    }
  }
  
  return <>{columns}</>;
}

function DayLabels() {
  const days = ['S', 'S', 'F', 'T', 'W', 'T', 'M'];
  const cols = 7;
  const spacing = 0.265;
  
  return (
    <>
      {days.map((day, col) => (
        <Text
          key={`day-${col}`}
          position={[
            1.2,
            0.15,
            (col - cols/2) * spacing
          ]}
          rotation={[0, Math.PI / 2, 0]}
          scale={0.15}
          color="#216e39"
        >
          {day}
        </Text>
      ))}
    </>
  );
}

function YouIndicator() {
  const materialRef = useRef();
  
  useFrame((state) => {
    if (materialRef.current) {
      // Animate opacity between 0.5 and 1 using a sine wave
      materialRef.current.opacity = 0.75 + Math.sin(state.clock.elapsedTime * 2) * 0.25;
    }
  });

  return (
    <group position={[1, 1.5, 2.0]}>
      {/* Blue dot */}
      <mesh>
        <sphereGeometry args={[0.08, 32, 32]} />
        <meshStandardMaterial 
          ref={materialRef}
          color="#007AFF" 
          emissive="#007AFF" 
          emissiveIntensity={0.5}
          transparent={true}
        />
      </mesh>
      
      {/* "You" text wrapped in Billboard component */}
      <Billboard>
        {/* White background rectangle */}
        <mesh position={[0, 0.3, -0.01]}>
          <planeGeometry args={[0.45, 0.2]} />
          <shaderMaterial
            transparent
            uniforms={{
              radius: { value: 0.05 },
              size: { value: [0.45, 0.2] },
              borderWidth: { value: 0.01 },
              borderColor: { value: [0, 0, 0] }  // #007AFF in RGB
            }}
            vertexShader={`
              varying vec2 vUv;
              void main() {
                vUv = uv;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
              }
            `}
            fragmentShader={`
              varying vec2 vUv;
              uniform float radius;
              uniform vec2 size;
              uniform float borderWidth;
              uniform vec3 borderColor;

              float roundedRectangle(vec2 uv, vec2 size, float radius) {
                vec2 q = abs(uv) - size + radius;
                return min(max(q.x, q.y), 0.0) + length(max(q, 0.0)) - radius;
              }

              void main() {
                vec2 uv = (vUv - 0.5) * size;
                float d = roundedRectangle(uv, size * 0.5, radius);
                float border = roundedRectangle(uv, size * 0.5 - borderWidth, radius - borderWidth);
                
                vec4 borderMask = vec4(borderColor, 1.0 - step(0.0, d));
                vec4 fillMask = vec4(1.0, 1.0, 1.0, 1.0 - step(0.0, border));
                
                gl_FragColor = mix(borderMask, fillMask, step(0.0, -border));
              }
            `}
          />
        </mesh>
        <Text
          position={[0, 0.3, 0]}
          scale={0.2}
          color="black"
          anchorX="center"
          anchorY="middle"
        >
          You
        </Text>
      </Billboard>
    </group>
  );
}

function CameraAnimation() {
  const { camera } = useThree();
  const animationComplete = useRef(false);
  
  useFrame((state) => {
    if (animationComplete.current) return;
    
    const startPos = 0.1;
    const endPos = 5.2;
    const duration = 5; // seconds
    const t = Math.min(state.clock.elapsedTime / duration, 1);
    
    // Smooth easing function
    const eased = 1 - Math.pow(1 - t, 3);
    const currentPos = startPos + (endPos - startPos) * eased;
    
    camera.position.set(currentPos, currentPos, currentPos);
    
    // Mark animation as complete when done
    if (t === 1) {
      animationComplete.current = true;
    }
  });
  
  return null;
}

export default function Scene() {
  return (
    <Canvas 
      camera={{ position: [2, 2, 2], fov: 45 }}
      style={{ background: '#ecefde' }}
      shadows
    >
      <CameraAnimation />
      <ambientLight intensity={0.5} />

      {/* Primary directional light - creates main shadows and highlights */}
      <directionalLight 
        position={[10, 10, 5]} 
        intensity={2} 
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-far={50}
        shadow-camera-left={-10}
        shadow-camera-right={10}
        shadow-camera-top={10}
        shadow-camera-bottom={-10}
      />

      {/* Secondary directional light - fills shadows from opposite direction */}
      <directionalLight 
        position={[-10, 10, -5]} 
        intensity={0.2}
      />

      {/* Top fill light - reduces harsh shadows and brightens top surfaces */}
      <directionalLight 
        position={[0, 10, 0]} 
        intensity={0.05}
      />
      
      <Suspense fallback={null}>
        <CircularPlatform />
        <ColumnsGrid />
        <DayLabels />
        <YouIndicator />

        {/* Place chairs on the left side of the column grid */}
        <Model
          url="/models/office_chair_008/office_chair_008.gltf"
          position={[1, 0, 2.0]}
          rotation={[0, 3.2, 0]}
          scale={1.5}
        />
        <Model
          url="/models/office_chair_020/office_chair_020.gltf"
          position={[-1.2, 0, 2]}
          rotation={[0, 3.1, 0]}
          scale={1.5}
        />

        {/* Place chairs on the right side of the column grid */}
        <Model
          url="/models/office_chair_010/office_chair_008.gltf"
          position={[-1.2, 0, -2.0]}
          rotation={[0, 0, 0]}
          scale={1.5}
        />
        <Model
          url="/models/office_chair_030/office_chair_020.gltf"
          position={[1, 0, -2]}
          rotation={[0, 0, 0]}
          scale={1.5}
        />
      </Suspense>
      <OrbitControls 
        maxPolarAngle={Math.PI / 2.5}
        minPolarAngle={Math.PI / 4}
      />
    </Canvas>
  );
}
